import { faCheck } from '@fortawesome/pro-regular-svg-icons'
import { Icon } from '@seiue/ui'
import { Progress, theme } from 'antd'
import React from 'react'
import styled, { css } from 'styled-components'

import { CourseOutlineTaskTypeEnum } from 'packages/sdks-next/growp'
import { getTheme } from 'packages/theme'

import { store, userThemeColorAtom } from '@/store'

import { courseOutlineTaskTypeEnumToLabel } from '../../utils'
import { Blank } from '../Layout'

/**
 * 树节点
 *
 * @param param0 - 参数
 * @param param0.isSelected - 是否选中
 * @param param0.onClick - 点击回调
 * @param param0.name - 名称
 * @param param0.isParent - 是否为父节点
 * @param param0.isSelectedChildren - 是否有选中的子节点
 * @param param0.type - 类型
 * @param param0.isPreview - 是否为预览模式
 * @param param0.isFinished - 是否完成
 * @param param0.percent - 进度
 * @returns 树节点
 */
export const TreeNode: React.FC<{
  isSelected: boolean
  onClick: () => void
  name: string
  isParent: boolean
  isSelectedChildren: boolean
  type?: CourseOutlineTaskTypeEnum
  isPreview?: boolean
  isFinished?: boolean
  percent?: number
}> = ({
  isSelected,
  onClick,
  name,
  isParent,
  isSelectedChildren,
  type,
  isPreview,
  isFinished,
  percent,
}) => {
  const userThemeColor = store.get(userThemeColorAtom)
  const theme = getTheme(userThemeColor)

  return (
    <TreeNodeWrapper
      $isSelected={isSelected}
      onClick={onClick}
      $isParent={isParent}
      $isSelectedChildren={isSelectedChildren}
    >
      <Title
        $isSelected={isSelected}
        $isParent={isParent}
        $isSelectedChildren={isSelectedChildren}
      >
        {name}
      </Title>
      <ExtraInfo>
        {type ? (
          <TypeLabel>{courseOutlineTaskTypeEnumToLabel.get(type)}</TypeLabel>
        ) : null}
        {isPreview || isParent || !isFinished ? (
          <Blank />
        ) : (
          <Icon
            icon={faCheck}
            color={theme.status.success.element}
          />
        )}
        {isParent && !isPreview ? (
          percent === 100 ? (
            <Icon
              icon={faCheck}
              color={theme.status.success.element}
            />
          ) : (
            <Progress
              percent={percent}
              type="circle"
              showInfo={false}
              trailColor={theme.brand._4}
              strokeColor={theme.brand._1}
              style={{ lineHeight: '28px' }}
              strokeWidth={14}
              width={12}
            />
          )
        ) : null}
      </ExtraInfo>
    </TreeNodeWrapper>
  )
}

export const commonWrapperCss = css`
  border-radius: 8px;
  padding: 6px 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 9px 12px;
  position: relative;
`

const TreeNodeWrapper = styled.div<{
  $isSelected: boolean
  $isParent: boolean
  $isSelectedChildren: boolean
}>`
  ${commonWrapperCss}
  background-color: ${({ $isSelected, $isParent, theme }) =>
    $isSelected && !$isParent ? theme.brand._5 : '#fff'};
  color: ${({ $isParent, $isSelectedChildren, theme }) =>
    $isParent && $isSelectedChildren ? theme.brand._1 : undefined};
  cursor: ${({ $isParent }) => ($isParent ? 'default' : 'pointer')};
  &:hover {
    background-color: ${({ $isParent, theme }) =>
      $isParent ? undefined : theme.brand._5};
  }
`

const Title = styled.div<{
  $isSelected: boolean
  $isParent: boolean
  $isSelectedChildren: boolean
}>`
  font-size: ${(props) => (props.$isParent ? ' 16px' : '14px')};
  font-weight: 400;
  color: ${(props) =>
    props.$isParent ? props.theme.text._1 : props.theme.text._2};
  color: ${({ $isParent, $isSelectedChildren, $isSelected, theme }) =>
    ($isParent && $isSelectedChildren) || $isSelected
      ? theme.brand._1
      : undefined};
`

const ExtraInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const TypeLabel = styled.div`
  font-size: 12px;
  color: ${(p) => p.theme.text._3};
  line-height: 20px;
`
